import React, { useEffect, useRef } from "react";
import { Camera, Check, Clock } from "lucide-react";

const STEPS = [
  {
    n: 1,
    label: "Face avant",
    img: "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/8ba84819ea41fd700c64debd936f77b9439d8a82dcec887c170f353444e3a760.jpeg",
  },
  {
    n: 2,
    label: "Côté droit",
    img: "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/c9ae204335d8a88e3fe80033567aa3ba761873c2da48d56d5b859f63200488a9.jpeg",
  },
  {
    n: 3,
    label: "Côté gauche",
    img: "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/cbf05555823108135d24a8586ff92c82b84cccad49dbca5c8dedc0ed61968c91.jpeg",
  },
  {
    n: 4,
    label: "Arrière",
    img: "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/3e897851fd95d9f3b3d7b41cdef470a48910c7489aa8e9b93ed412f1b66e6d61.jpeg",
  },
  {
    n: 5,
    label: "Intérieur & kilométrage",
    img: "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/71d3153f25a14df0c3261793dcbe2880799b8707b052e4e4f6f22b91a263990a.jpeg",
  },
];

export default function InspectionFilmstrip() {
  const wrapRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    let raf = null;
    const update = () => {
      raf = null;
      const wrap = wrapRef.current;
      const track = trackRef.current;
      if (!wrap || !track) return;
      const total = Math.max(1, wrap.offsetHeight - window.innerHeight);
      const rect = wrap.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, -rect.top / total));
      const max = Math.max(0, track.scrollWidth - window.innerWidth + 48);
      track.style.transform = `translate3d(${-progress * max}px, 0, 0)`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section ref={wrapRef} className="relative bg-white" style={{ height: "300vh" }} data-testid="inspection-filmstrip-section">
      {/* Viewport épinglé : la page se fige, les photos défilent horizontalement */}
      <div className="sticky top-0 h-screen overflow-hidden flex flex-col bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-16 md:pt-20 w-full">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold mb-4 bg-violet-50 border border-violet-100 text-violet-700"
            style={{ animation: "pulseGlow 2.6s ease-in-out infinite" }}
            data-testid="day-chip-1100"
          >
            <Clock className="h-3 w-3" /> 11h00
            <span className="font-medium text-slate-500">· Remise des clés</span>
          </div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-violet-600 mb-3">États des lieux digitaux</div>
          <div className="grid lg:grid-cols-2 gap-6 items-end">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight leading-tight text-slate-900">
              Un parcours photo guidé,
              <br />
              étape par étape.
            </h2>
            <p className="text-sm md:text-base text-slate-600 leading-relaxed">
              Face avant, côtés, arrière, intérieur : le logiciel guide le loueur zone par zone.
              Chaque photo est horodatée et attachée au contrat de location — au retour, les deux états des lieux sont comparés automatiquement.
            </p>
          </div>
        </div>

        {/* Bande photo ancrée en bas de l'écran, aérée */}
        <div className="relative mt-auto pt-10 pb-10 md:pb-14">
          <div ref={trackRef} className="flex gap-5 md:gap-7 px-6 will-change-transform w-max">
            {STEPS.map((s) => (
              <figure
                key={s.n}
                className="relative shrink-0 w-[72vw] sm:w-[440px] md:w-[520px] h-[240px] sm:h-[290px] md:h-[330px] rounded-3xl overflow-hidden ring-1 ring-slate-900/10 shadow-[0_24px_60px_-24px_rgba(15,23,42,0.35)]"
                data-testid={`inspection-step-${s.n}`}
              >
                <img
                  src={s.img}
                  alt={`État des lieux — ${s.label}`}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/5 to-transparent" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur border border-white/50 shadow-sm">
                  <span className="h-5 w-5 rounded-full bg-violet-600 text-white text-[10px] font-bold flex items-center justify-center">{s.n}</span>
                  <span className="text-xs font-semibold text-slate-900">{s.label}</span>
                </div>
                <figcaption className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 text-white text-sm font-medium">
                    <Camera className="h-4 w-4 text-violet-300" /> Photo horodatée
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/90 text-white text-[11px] font-semibold">
                    <Check className="h-3 w-3" strokeWidth={3} /> Enregistrée
                  </span>
                </figcaption>
              </figure>
            ))}
            {/* Closing card */}
            <div className="relative shrink-0 w-[72vw] sm:w-[440px] md:w-[520px] h-[240px] sm:h-[290px] md:h-[330px] rounded-3xl overflow-hidden bg-slate-950 flex flex-col items-center justify-center text-center px-10" data-testid="inspection-step-final">
              <div aria-hidden className="absolute -right-16 -bottom-24 h-72 w-72 rounded-full bg-violet-600 opacity-30 blur-3xl" />
              <div className="relative">
                <div className="h-12 w-12 mx-auto rounded-2xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
                  <Check className="h-6 w-6 text-violet-300" strokeWidth={2.5} />
                </div>
                <div className="mt-5 font-heading text-2xl md:text-3xl font-medium tracking-tight text-white">
                  État des lieux terminé.
                </div>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed max-w-xs mx-auto">
                  Comparé automatiquement au retour du véhicule. En cas de litige, la preuve est déjà là.
                </p>
              </div>
            </div>
          </div>
          {/* Edge fades */}
          <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-28 bg-gradient-to-r from-white to-transparent" />
          <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-28 bg-gradient-to-l from-white to-transparent" />
        </div>
      </div>
    </section>
  );
}
